import { useCallback, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, Header, Skeleton, StatusMessage, Tab, TabNav } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { SearchX, ShieldOff, TriangleAlert, UserCog, UserX } from 'lucide-react';

import { MEMBER_STATUS, PENDING_MEMBER_STATUSES, USER_TYPE } from '../../constants/copy';
import { DEFAULT_TAB, MEMBER_DETAIL_TABS } from '../../constants/memberDetail';
import { PERMISSIONS } from '../../constants/permissions';
import { useActor } from '../../hooks/usePermission';
import { useMember } from '../../hooks/useMember';
import { useMemberActions } from '../../hooks/useMemberActions';
import { getFirstName } from '../../utils/memberDisplay';
import { getMemberActions, getMemberStatusView } from '../../utils/memberStatusView';
import MemberGroupsTab from '../../pages/MemberGroupsTab';
import MemberResourcesTab from '../../pages/MemberResourcesTab';
import MemberRolesTab from '../../pages/MemberRolesTab';
import MemberSecurityTab from '../../pages/MemberSecurityTab';
import MemberStudiosTab from '../../pages/MemberStudiosTab';
import ConfirmActionModal from './ConfirmActionModal';
import EditEmailModal from './EditEmailModal';
import MemberInfoCard from './MemberInfoCard';
import MemberQuickActions from './MemberQuickActions';
import MemberStateBanner from './MemberStateBanner';
import DeleteContactConfirmModal from './DeleteContactConfirmModal';
import DeleteUserConfirmModal from './DeleteUserConfirmModal';
import RemovedBanner from './RemovedBanner';
import RemoveMemberModal from './RemoveMemberModal';
import FullPageFrame from '../shared/FullPageFrame';
import { getBackTo } from '../../utils/groupPaths';

const ERROR_ICON = { notFound: SearchX, forbidden: ShieldOff, failed: TriangleAlert };

/** Dialog id → how it is confirmed (`variant`) and which action it runs. */
const CONFIRM_ICON = { suspend: UserX, makeAdmin: UserCog, makeMember: UserCog };
const CONFIRM_VARIANT = { suspend: 'danger', makeAdmin: 'primary', makeMember: 'primary' };

/**
 * Body of the member detail: loads the member, renders the card, banner, quick actions, tabs
 * and the confirm dialogs, including the two-step Remove flow and the Removed state.
 *
 * @param {object} props
 * @param {string} props.memberId
 * @param {string} [props.tab] Route segment; a missing or unknown one redirects to the default tab.
 * @param {string} props.basePath Where the host mounted People & Access.
 * @param {boolean} [props.showDepartment]
 */
function MemberDetailView({ memberId, tab, basePath, showDepartment }) {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  // Whoever linked here (e.g. a group) can set `state.backTo`; it survives the tab changes.
  const { state: linkState } = useLocation();
  const backTo = getBackTo(linkState);
  const actor = useActor();
  const { member, isLoading, error, setMember, refetch } = useMember(memberId);
  const [dialog, setDialog] = useState(null);

  const actions = useMemberActions({ member: member ?? { id: memberId, displayName: '' }, onChange: setMember });

  const closeDialog = useCallback(() => setDialog(null), []);
  const goBack = useCallback(
    () => navigate(backTo ?? `${basePath}/members`),
    [navigate, basePath, backTo],
  );
  const goToTab = useCallback(
    (id) => navigate(`${basePath}/members/${memberId}/${id}`, { state: linkState }),
    [navigate, basePath, memberId, linkState],
  );
  const askSuspend = useCallback(() => setDialog('suspend'), []);
  const askType = useCallback((userType) => setDialog(userType === USER_TYPE.ADMIN ? 'makeAdmin' : 'makeMember'), []);
  const askEmail = useCallback(() => setDialog('editEmail'), []);
  // Somebody who never signed in is a contact: deleting them has its own, lighter confirmation.
  const isContact = member ? PENDING_MEMBER_STATUSES.includes(member.status) : false;
  const askRemove = useCallback(() => setDialog(isContact ? 'deleteContact' : 'remove'), [isContact]);
  const askDelete = useCallback(() => setDialog('deleteUser'), []);
  const deleteUser = useCallback(async () => {
    const ok = await actions.deleteUser();
    if (ok) navigate(`${basePath}/members`);
    return ok;
  }, [actions, navigate, basePath]);

  const memberActions = useMemo(
    () =>
      member
        ? getMemberActions({
            uiStatus: member.status,
            userType: member.userType,
            actorUserType: actor?.user_type,
            actorCanRemoveAdmin: Boolean(actor?.permissions?.includes(PERMISSIONS.MEMBERS_REMOVE_ADMIN)),
          })
        : null,
    [member, actor],
  );

  const confirmations = useMemo(() => {
    const name = member?.displayName ?? '';
    const text = (id) => ({
      title: t(`memberDetail.confirm.${id}.title`, { name }),
      message: t(`memberDetail.confirm.${id}.message`, { name, first: getFirstName(name) }),
      confirmLabel: t(`memberDetail.confirm.${id}.confirm`),
      confirmVariant: CONFIRM_VARIANT[id],
      icon: CONFIRM_ICON[id],
    });
    return {
      suspend: { ...text('suspend'), onConfirm: actions.suspend },
      makeAdmin: { ...text('makeAdmin'), onConfirm: () => actions.changeType(USER_TYPE.ADMIN) },
      makeMember: { ...text('makeMember'), onConfirm: () => actions.changeType(USER_TYPE.MEMBER) },
    };
  }, [t, member?.displayName, actions]);

  if (!MEMBER_DETAIL_TABS.some(({ id }) => id === tab)) {
    return <Navigate to={`${basePath}/members/${memberId}/${DEFAULT_TAB}`} state={linkState} replace />;
  }

  if (isLoading) {
    return (
      <FullPageFrame>
        <div className="flex flex-col gap-4" data-testid="member-detail-loading">
          <Skeleton width="220px" height="20px" />
          <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
            <Skeleton height="360px" borderRadius="12px" />
            <Skeleton height="360px" borderRadius="12px" />
          </div>
        </div>
      </FullPageFrame>
    );
  }

  if (error || !member) {
    // Anything that is not a known kind (an Error object, an unexpected string) is a generic failure
    // (CT-5832: an undefined icon here crashed the whole app with React error #130).
    const kind = error && ERROR_ICON[error] ? error : 'failed';
    return (
      <FullPageFrame>
        <div className="flex flex-col items-center gap-3" data-testid="member-detail-error">
          <StatusMessage
            icon={ERROR_ICON[kind]}
            title={t(`memberDetail.state.${kind}.title`)}
            subtitle={t(`memberDetail.state.${kind}.subtitle`)}
          />
          {kind === 'failed' ? (
            <Button variant="secondary" size="md" onClick={refetch}>
              {t('memberDetail.state.retry')}
            </Button>
          ) : (
            <Button variant="secondary" size="md" onClick={goBack}>
              {t('memberDetail.state.backToList')}
            </Button>
          )}
        </div>
      </FullPageFrame>
    );
  }

  const view = getMemberStatusView(member.status);
  const confirmation = confirmations[dialog];

  return (
    <FullPageFrame>
      <div className="flex flex-col gap-6">
        <Header
          title={t('memberDetail.back')}
          titleClassName="!text-lg !font-semibold"
          showBackButton
          onBack={goBack}
          backButtonAriaLabel={t('memberDetail.backAria')}
          showDescription={false}
          showTag={false}
          showPrimaryCta={false}
        />
        <div className="grid gap-6 lg:grid-cols-[300px_1fr] lg:items-start">
          <div className="flex flex-col gap-4">
            <MemberInfoCard
              member={member}
              userTypeAction={memberActions.userTypeAction}
              onChangeType={askType}
              showDepartment={showDepartment}
            >
              <MemberQuickActions
                actions={memberActions}
                onResetPassword={actions.resetPassword}
                onSuspend={askSuspend}
                onRemove={askRemove}
                onReactivate={actions.reactivate}
                onRevoke={actions.revokeInvitation}
                onDelete={askDelete}
              />
            </MemberInfoCard>
          </div>
          <div className="flex min-w-0 flex-col gap-6">
            {member.status === MEMBER_STATUS.REMOVED ? <RemovedBanner name={getFirstName(member.displayName)} /> : null}
            <MemberStateBanner
              banner={view.banner}
              member={member}
              onEditEmail={askEmail}
              onResend={actions.resendInvitation}
              onSend={actions.sendInvitation}
            />
            <TabNav ariaLabel={t('memberDetail.tabsAriaLabel')} className="scrollbar-aimsos-auto-sm max-w-full overflow-x-auto">
              {MEMBER_DETAIL_TABS.map(({ id, labelKey }) => (
                <Tab key={id} selected={tab === id} onClick={() => goToTab(id)}>
                  {t(labelKey)}
                </Tab>
              ))}
            </TabNav>
            {tab === 'studios' && <MemberStudiosTab member={member} />}
            {tab === 'roles' && <MemberRolesTab member={member} basePath={basePath} />}
            {tab === 'groups' && <MemberGroupsTab member={member} basePath={basePath} />}
            {tab === 'resources' && <MemberResourcesTab member={member} />}
            {tab === 'security' && <MemberSecurityTab member={member} />}
          </div>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={Boolean(confirmation)}
        onClose={closeDialog}
        title={confirmation?.title ?? ''}
        message={confirmation?.message ?? ''}
        confirmLabel={confirmation?.confirmLabel ?? ''}
        confirmVariant={confirmation?.confirmVariant}
        icon={confirmation?.icon}
        iconTone="Alert"
        onConfirm={confirmation?.onConfirm ?? (() => Promise.resolve(true))}
      />
      <DeleteContactConfirmModal
        isOpen={dialog === 'deleteContact'}
        name={member.displayName}
        onClose={closeDialog}
        onConfirm={deleteUser}
      />
      <RemoveMemberModal
        isOpen={dialog === 'remove'}
        member={member}
        onRemove={actions.remove}
        onClose={closeDialog}
      />
      <DeleteUserConfirmModal
        isOpen={dialog === 'deleteUser'}
        name={member.displayName}
        onClose={closeDialog}
        onConfirm={deleteUser}
      />
      {dialog === 'editEmail' ? (
        <EditEmailModal
          memberId={member.id}
          email={member.email}
          resends={member.status !== MEMBER_STATUS.NOT_INVITED}
          onClose={closeDialog}
          onSave={actions.editEmail}
        />
      ) : null}
    </FullPageFrame>
  );
}

export default MemberDetailView;
